import { useEffect, useState, memo, useMemo, useCallback } from "react";
import { createValuation, fetchFtmEarnings } from "../service";
import { capitalizeFirstLetter, formatMoney } from "../../../utils";
import DivSpinner from "../../../components/divSpinner";
import { theme } from "../../../theme";
import { commonSwal } from "../../../components/swalAlert/swalAlert";
import toast from "react-hot-toast";

/*
 Memoized Row Component
*/
const PaymentRow = memo(({ item }) => {
    return (
        <tr>
            <td>
                <h6 className="text-sm font-weight-normal mb-0">
                    {capitalizeFirstLetter(item.type)}
                </h6>
            </td>

            <td className="p-2 text-end">
                <h6 className="text-sm font-weight-normal mb-0">
                    {formatMoney(item.earnings)}
                </h6>
            </td>

            <td className="p-2 text-end">
                <h6 className="text-sm font-weight-normal mb-0">
                    {formatMoney(item.last_valuation_paid)}
                </h6>
            </td>

            <td className="align-middle text-sm p-2 text-end">
                <h6 className="text-sm font-weight-normal mb-0">
                    {formatMoney(item.valuation_difference)}
                </h6>
            </td>
        </tr>
    );
});

const PaymentInformation = ({ ftm_id, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [ftm, setFtm] = useState(null);
    const [paymentInformation, setPaymentInformation] = useState([]);

    const getFtmEarnings = useCallback(async () => {
        setLoading(true);

        try {
            const response = await fetchFtmEarnings(ftm_id);

            if (response?.status === "Success") {
                setPaymentInformation(
                    response?.data?.payment_information ?? []
                );
                setFtm(response?.data?.ftm ?? null);
            }
        } catch (error) {
            console.error("Error fetching FtmEarnings data:", error);
        } finally {
            setLoading(false);
        }
    }, [ftm_id]);

    useEffect(() => {
        if (!ftm_id) return;
        getFtmEarnings();
    }, [ftm_id, getFtmEarnings]);

    /*
      Calculate totals
    */
    const totals = useMemo(() => {
        return paymentInformation.reduce(
            (acc, item) => {
                acc.earnings += Number(item.earnings || 0);
                acc.valuation += Number(item.last_valuation_paid || 0);
                acc.item_valuation_difference += Number(item.valuation_difference || 0);
                return acc;
            },
            { earnings: 0, valuation: 0, item_valuation_difference: 0 }
        );
    }, [paymentInformation]);

    const handleCreateValuation = useCallback(() => {
        commonSwal({
            notificationType: "CREATE_VALUATION",
            preConfirm: async () => createValuation(ftm_id),
            isConfirmed: (result) => {
                if (result?.isConfirmed && result?.value?.status === "Success") {
                    toast.success(result?.value?.message);

                    getFtmEarnings(); // self refresh
                    onRefresh?.();     // 🔥 trigger Report refresh
                }
            },
        });
    }, [ftm_id, getFtmEarnings, onRefresh]);

    return (
        <div className="card mb-4">
            <div className="card-header pb-0">
                <h6 className="mb-0">
                    Payment Information - {ftm?.forename} {ftm?.surname}
                </h6>
            </div>

            <div className="card-body p-3" style={{ minHeight: "260px" }}>
                {loading ? (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: "200px" }}
                    >
                        <DivSpinner />
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            <div className="table-responsive">
                                <table className="table align-items-center">

                                    <thead>
                                        <tr>
                                            <th className="text-secondary text-sm font-weight-bold p-2">
                                                Summary
                                            </th>
                                            <th className="text-secondary text-sm font-weight-bold p-2 text-end">
                                                Earnings
                                            </th>
                                            <th className="text-secondary text-sm font-weight-bold p-2 text-end">
                                                Last valuation
                                            </th>
                                            <th className="text-secondary text-sm font-weight-bold p-2 text-end">
                                                Difference
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paymentInformation.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center text-sm py-3">
                                                    No payment data available
                                                </td>
                                            </tr>
                                        ) : (
                                            paymentInformation.map((item) => (
                                                <PaymentRow key={item.item_balance_type} item={item} />
                                            ))
                                        )}

                                    </tbody>

                                    <tfoot>
                                        {/* Totals Row */}
                                        {paymentInformation.length > 0 && (
                                            <tr className="total-row" style={{ fontSize: '50px' }}>
                                                <td>
                                                    <h6 className="font-weight-bold mb-0">
                                                        Total
                                                    </h6>
                                                </td>

                                                <td className="p-2 text-end">
                                                    <h6 className="font-weight-bold mb-0">
                                                        {formatMoney(totals.earnings)}
                                                    </h6>
                                                </td>

                                                <td className="p-2 text-end">
                                                    <h6 className="font-weight-bold mb-0">
                                                        {formatMoney(totals.valuation)}
                                                    </h6>
                                                </td>

                                                <td className="p-2 text-end">
                                                    <h6
                                                        className={`font-weight-bold mb-0 ${totals.item_valuation_difference >= 0
                                                            ? "text-success"
                                                            : "text-danger"
                                                        }`}
                                                    >
                                                        {formatMoney(totals.item_valuation_difference)}
                                                    </h6>
                                                </td>
                                            </tr>
                                        )}
                                    </tfoot>

                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="card-footer d-flex justify-content-end pt-0">
                <button
                    className={`btn bg-gradient-${theme.main} mb-0 btn-sm`}
                    type="button"
                    name="button"
                    onClick={handleCreateValuation}
                    disabled={totals.item_valuation_difference === 0}
                >
                    Create valuation
                </button>
            </div>
        </div>
    );
};

export default PaymentInformation;